import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Loader2, Trash2, X } from 'lucide-react'
import apiClient from '@/api/client'
import type { PurchaseOrderAttachment } from '@/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatDateTimeHuman } from '@/lib/utils'
import { shrinkPhoto } from '@/lib/shrinkPhoto'
import { getStoredUserRole } from '@/lib/staff-roles'
import { IM } from '@/components/store/inventory/itemMasterStrings'
import { canDeletePoPhoto } from './purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const PS = PURCHASING_STRINGS.photos

/**
 * Photos of the vendor's delivery slip on a PO (kitchen flow, migration 136).
 * "Take photo" opens the rear camera on a phone/tablet (a file picker on a
 * PC); the photo is shrunk on the device, then stored with the PO. Rendered
 * only when the caller has checked useKitchenFlowEnabled().
 */
export function PoPhotos({ poId, poStatus, showToast }: {
  poId: string
  /** The order's status: once it is closed only an admin may remove a photo
   *  (pilot Batch 5). Omitted while receiving — the order is still open. */
  poStatus?: string
  showToast: (t: 'success' | 'error', m: string) => void
}) {
  const qc = useQueryClient()
  const key = ['purchaseOrderAttachments', poId]
  const inputRef = useRef<HTMLInputElement>(null)
  const [viewing, setViewing] = useState<PurchaseOrderAttachment | null>(null)
  const { data, isError, refetch } = useQuery({ queryKey: key, queryFn: () => apiClient.listPurchaseOrderAttachments(poId).then((r) => r.data ?? []) })
  const photos = data ?? []
  const mayDelete = canDeletePoPhoto(getStoredUserRole(), poStatus)
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const upload = useMutation({
    mutationFn: async (file: File) => apiClient.addPurchaseOrderAttachment(poId, await shrinkPhoto(file)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); showToast('success', IM.photos.added) },
    onError: (e: Error) => showToast('error', e.message),
  })
  const remove = useMutation({
    mutationFn: (attId: string) => apiClient.deletePurchaseOrderAttachment(poId, attId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: key }); setViewing(null); setConfirmId(null); showToast('success', IM.photos.removed) },
    onError: (e: Error) => showToast('error', e.message),
  })

  return (
    <div className="space-y-2 rounded-lg border-2 border-dashed p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-semibold">{IM.photos.heading}</span>
        <Button type="button" variant="outline" disabled={upload.isPending} onClick={() => inputRef.current?.click()}>
          {upload.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}
          {IM.photos.take}
        </Button>
        <input
          ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) upload.mutate(file)
          }}
        />
      </div>
      {isError && photos.length === 0 ? (
        <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-danger-fg">{PURCHASING_STRINGS.errors.photos}</span>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>{PURCHASING_STRINGS.errors.retry}</Button>
        </div>
      ) : photos.length === 0 ? (
        <p className="text-sm text-muted-foreground">{IM.photos.none}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <button key={p.id} type="button" className="h-20 w-20 overflow-hidden rounded-md border" onClick={() => setViewing(p)} aria-label={IM.photos.open}>
              <AuthImage poId={poId} att={p} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      {viewing && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-3 bg-black/80 p-4" onClick={() => setViewing(null)}>
          <div className="flex w-full max-w-3xl items-center justify-between text-white" onClick={(e) => e.stopPropagation()}>
            <span className="text-sm">{formatDateTimeHuman(viewing.created_at)}{viewing.uploaded_by_name ? ` · ${viewing.uploaded_by_name}` : ''}</span>
            <div className="flex gap-2">
              {mayDelete ? (
                <Button type="button" variant="destructive" disabled={remove.isPending} onClick={() => { setConfirmId(viewing.id); setViewing(null) }}>
                  <Trash2 className="mr-1.5 h-4 w-4" /> {IM.photos.remove}
                </Button>
              ) : (
                <span className="self-center text-xs text-white/80">{PS.locked}</span>
              )}
              <Button type="button" variant="secondary" className="h-10 w-10 p-0" onClick={() => setViewing(null)} aria-label="Close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <AuthImage poId={poId} att={viewing} className="max-h-[80vh] max-w-full rounded-md object-contain" />
        </div>
      )}
      <Dialog open={confirmId !== null} onOpenChange={(o) => !o && !remove.isPending && setConfirmId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{PS.confirmTitle}</DialogTitle>
            <DialogDescription>{PS.confirmBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" disabled={remove.isPending} onClick={() => setConfirmId(null)}>{PS.cancel}</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={() => confirmId && remove.mutate(confirmId)}>{PS.confirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** An <img> for a protected photo: fetched through the API client as a blob. */
function AuthImage({ poId, att, className }: { poId: string; att: PurchaseOrderAttachment; className?: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    let revoked = false
    let objectUrl: string | null = null
    apiClient.getPurchaseOrderAttachmentBlob(poId, att.id).then((blob) => {
      if (revoked) return
      objectUrl = URL.createObjectURL(blob)
      setUrl(objectUrl)
    }).catch(() => setUrl(null))
    return () => {
      revoked = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [poId, att.id])
  if (!url) return <div className={`${className ?? ''} animate-pulse bg-muted`} />
  return <img src={url} alt={IM.photos.alt} className={className} />
}
